import { z } from "zod";
import { projectSubjectContextForAsk, SUBJECT_ASK_PROMPT_CHAR_BUDGET, } from "./subject-ask-projection.js";
import { connectedContentIdentityInput, connectedDecisionCitationId, connectedFactCitationId, } from "./subject-connected-knowledge.js";
import { factsAreCompeting, isOpenQuestionResolved } from "./subject-intelligence.js";
export const CURRENT_POSITION_MAX_SENTENCES = 8;
export const CURRENT_POSITION_TARGET_SENTENCES = 5;
export const CurrentPositionStatusSchema = z.enum(["sufficient", "insufficient"]);
export const CurrentPositionOriginSchema = z.enum(["skeleton", "generated"]);
export const CurrentPositionItemSchema = z.object({
    text: z.string().min(1).max(800),
    citationIds: z.array(z.string().min(1)).max(12),
});
export const CurrentPositionModelOutputSchema = z.object({
    sentences: z.array(CurrentPositionItemSchema).max(CURRENT_POSITION_MAX_SENTENCES),
});
export const CurrentPositionResultSchema = z.object({
    status: CurrentPositionStatusSchema,
    origin: CurrentPositionOriginSchema,
    generatedAt: z.string().datetime(),
    subjectId: z.string().min(1),
    bundleVersion: z.literal(1),
    bundleIdentity: z.string().min(1),
    truncated: z.boolean(),
    items: z.array(CurrentPositionItemSchema),
    insufficientReason: z.string().nullable(),
});
export const SubjectCurrentPositionQuerySchema = z.object({
    refresh: z.enum(["1", "true"]).optional(),
});
export const CURRENT_POSITION_DISCIPLINE = [
    "CURRENT POSITION DISCIPLINE:",
    "Write 3–5 concise sentences answering where the organisation currently stands on this Subject.",
    "Do not restate the Subject identity summary. Prefer established present position, current decisions or rulings, significant qualifications, and unresolved matters when material.",
    "Do not list facts merely because they exist. Use only the supplied Subject context. Do not speculate. Do not invent facts, prices, terms, or resolutions.",
    "DIRECT SUBJECT KNOWLEDGE is canonical on the pinned Subject. CONNECTED SUBJECT KNOWLEDGE is recorded on named origin Subjects. SYNTHESIS may combine them to state the pinned Subject's present position.",
    "Material numbers or claims that appear only under CONNECTED KNOWLEDGE must name the origin Subject once (for example “Cellara’s current factory-gate cost is …”). Do not write those claims as facts recorded on the pinned Subject.",
    "Do not repeat the origin name in every sentence; after the first attributed claim, later sentences may refer to that origin only if unambiguous. Do not write robotic “According to X” on every line.",
    "Cite connected-fact:, connected-decision:, or connected-evidence: for connected claims. Presence of an origin does not confer extra programme membership or further scope.",
    "Canonical facts and explicit decisions outrank evidence. Associations and related Subjects are context, not proof.",
    "Present later asOf facts as current; do not describe historical same-key facts as the present position.",
    "If competing facts share a period, say they remain unresolved. Do not pick a winner.",
    "Do not treat an open question as answered. Do not infer CRM identity from names.",
    "Each sentence MUST include citationIds drawn from the supplied [citationId] tokens.",
    'Return JSON only: {"sentences":[{"text":"...","citationIds":["..."]}]}',
].join(" ");
function asOfDay(value) {
    if (!value?.trim())
        return null;
    if (/^\d{4}-\d{2}-\d{2}/.test(value))
        return value.slice(0, 10);
    const parsed = Date.parse(value);
    if (Number.isNaN(parsed))
        return null;
    return new Date(parsed).toISOString().slice(0, 10);
}
function fnv1aHex(input) {
    let hash = 2166136261;
    for (let i = 0; i < input.length; i += 1) {
        hash ^= input.charCodeAt(i);
        hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0).toString(16).padStart(8, "0");
}
function clip(text, max) {
    const trimmed = text.trim();
    if (trimmed.length <= max)
        return trimmed;
    return `${trimmed.slice(0, Math.max(0, max - 1))}…`;
}
export function selectCurrentFacts(facts) {
    const byKey = new Map();
    for (const fact of facts) {
        const list = byKey.get(fact.key) ?? [];
        list.push(fact);
        byKey.set(fact.key, list);
    }
    const out = [];
    for (const [key, group] of [...byKey.entries()].sort(([a], [b]) => a.localeCompare(b))) {
        const days = group.map((row) => asOfDay(row.asOf)).filter((day) => Boolean(day));
        const latestDay = days.sort().at(-1) ?? null;
        const current = latestDay ? group.filter((row) => asOfDay(row.asOf) === latestDay) : group;
        let competing = false;
        for (let i = 0; i < current.length; i += 1) {
            for (let j = i + 1; j < current.length; j += 1) {
                const left = current[i];
                const right = current[j];
                if (left && right && factsAreCompeting(left, right))
                    competing = true;
            }
        }
        out.push({
            key,
            competing,
            facts: current.map((row) => ({
                citationId: row.citationId,
                value: row.value,
                unit: row.unit ?? null,
                asOf: row.asOf,
            })),
        });
    }
    return out;
}
/**
 * Sufficient when a present recorded position can be stated from canonical
 * material. Identity, associations, related Subjects, and open questions alone
 * are not a position.
 */
export function assessCurrentPositionSufficiency(bundle) {
    const canonical = bundle.canonical;
    if (canonical.facts.length > 0)
        return { status: "sufficient", reason: null };
    if (canonical.decisions.length > 0)
        return { status: "sufficient", reason: null };
    if (canonical.summary.text.trim())
        return { status: "sufficient", reason: null };
    if (canonical.body.text.trim())
        return { status: "sufficient", reason: null };
    if (canonical.superseded)
        return { status: "sufficient", reason: null };
    if (bundle.connected.facts.length > 0)
        return { status: "sufficient", reason: null };
    if (bundle.connected.origins.some((origin) => origin.decisions.length > 0)) {
        return { status: "sufficient", reason: null };
    }
    if (canonical.openQuestions.some((row) => !isOpenQuestionResolved(row.nodeId, canonical.related))) {
        return {
            status: "insufficient",
            reason: "Only unresolved questions are recorded; there is no present position.",
        };
    }
    return {
        status: "insufficient",
        reason: "Only identity or operational metadata is recorded; there is no present position.",
    };
}
export function collectCurrentPositionCitationIds(bundle) {
    const ids = new Set();
    const add = (id) => {
        if (id)
            ids.add(id);
    };
    const canonical = bundle.canonical;
    add(canonical.identity.citationId);
    add(canonical.summary.citationId);
    add(canonical.body.citationId);
    for (const fact of canonical.facts)
        add(fact.citationId);
    for (const decision of canonical.decisions)
        add(decision.citationId);
    for (const question of canonical.openQuestions)
        add(question.citationId);
    for (const evidence of canonical.evidence) {
        if (evidence.unavailableReason === "restricted")
            continue;
        add(evidence.citationId);
    }
    for (const excerpt of bundle.retrieval.excerpts) {
        if (excerpt.layer === "association")
            continue;
        if (excerpt.unavailableReason === "restricted")
            continue;
        add(excerpt.citationId);
    }
    for (const clipping of canonical.clippings)
        add(clipping.citationId);
    for (const excerpt of canonical.chatExcerpts)
        add(excerpt.citationId);
    for (const origin of bundle.connected.origins) {
        for (const fact of origin.facts) {
            add(connectedFactCitationId(origin.originSubjectId, fact.citationId));
            for (const evidence of fact.evidence)
                add(evidence.citationId);
        }
        for (const decision of origin.decisions) {
            add(connectedDecisionCitationId(origin.originSubjectId, decision.citationId));
        }
    }
    return ids;
}
export function subjectBundleContentIdentity(bundle) {
    const payload = JSON.stringify({
        bundleVersion: bundle.bundleVersion,
        subjectId: bundle.subjectId,
        truncated: bundle.truncated,
        canonical: bundle.canonical,
        attention: bundle.intelligence.attention,
        changes: bundle.intelligence.changes.map((row) => ({
            id: row.id,
            type: row.type,
            summary: row.summary,
            citationId: row.citationId,
            material: row.material,
        })),
        connected: connectedContentIdentityInput(bundle.connected),
    });
    return `bundle:${bundle.bundleVersion}:${fnv1aHex(payload)}:${fnv1aHex(payload.slice(Math.floor(payload.length / 2)))}`;
}
export function unresolvedOpenQuestions(bundle) {
    return bundle.canonical.openQuestions.filter((row) => !isOpenQuestionResolved(row.nodeId, bundle.canonical.related));
}
export function buildCurrentPositionSkeleton(bundle) {
    const items = [];
    const ident = bundle.canonical.identity;
    const sufficiency = assessCurrentPositionSufficiency(bundle);
    if (bundle.canonical.superseded) {
        items.push({
            text: `${ident.label} is recorded as superseded: ${bundle.canonical.superseded.reason}.`,
            citationIds: [ident.citationId],
        });
    }
    if (sufficiency.status === "insufficient") {
        items.push({
            text: sufficiency.reason ?? `No recorded present position for ${ident.label}.`,
            citationIds: [ident.citationId],
        });
        const open = unresolvedOpenQuestions(bundle);
        if (open.length > 0) {
            items.push({
                text: `Remains unresolved: ${open.map((row) => row.label).join("; ")}.`,
                citationIds: open.map((row) => row.citationId),
            });
        }
        return items.slice(0, CURRENT_POSITION_TARGET_SENTENCES);
    }
    for (const group of selectCurrentFacts(bundle.canonical.facts)) {
        if (group.competing) {
            const values = group.facts
                .map((fact) => `${fact.value}${fact.unit ? ` ${fact.unit}` : ""}`)
                .join(" vs ");
            items.push({
                text: `Recorded values for ${group.key.replaceAll("_", " ")} remain competing (${group.facts[0]?.asOf ?? "unknown as-of"}): ${values}. The current value is not settled.`,
                citationIds: group.facts.map((fact) => fact.citationId),
            });
            continue;
        }
        const fact = group.facts[0];
        if (!fact)
            continue;
        items.push({
            text: `Currently recorded ${group.key.replaceAll("_", " ")} is ${fact.value}${fact.unit ? ` ${fact.unit}` : ""} (as of ${fact.asOf}).`,
            citationIds: [fact.citationId],
        });
    }
    const decisions = [...bundle.canonical.decisions].sort((a, b) => b.at.localeCompare(a.at));
    const latest = decisions[0];
    if (latest) {
        items.push({
            text: `Latest recorded decision (${latest.at.slice(0, 10)}): ${clip(latest.text, 220)}`,
            citationIds: [latest.citationId],
        });
    }
    if (items.length < CURRENT_POSITION_TARGET_SENTENCES) {
        for (const origin of bundle.connected.origins) {
            for (const fact of origin.facts) {
                if (items.length >= CURRENT_POSITION_TARGET_SENTENCES)
                    break;
                items.push({
                    text: `${origin.label} currently records ${fact.key.replaceAll("_", " ")} of ${fact.value}${fact.unit ? ` ${fact.unit}` : ""} (as of ${fact.asOf}).`,
                    citationIds: [connectedFactCitationId(origin.originSubjectId, fact.citationId)],
                });
            }
            const originDecision = [...origin.decisions].sort((a, b) => b.at.localeCompare(a.at))[0];
            if (originDecision && items.length < CURRENT_POSITION_TARGET_SENTENCES) {
                items.push({
                    text: `${origin.label} latest recorded decision (${originDecision.at.slice(0, 10)}): ${clip(originDecision.text, 220)}`,
                    citationIds: [
                        connectedDecisionCitationId(origin.originSubjectId, originDecision.citationId),
                    ],
                });
            }
            if (items.length >= CURRENT_POSITION_TARGET_SENTENCES)
                break;
        }
    }
    const open = unresolvedOpenQuestions(bundle);
    if (open.length > 0) {
        items.push({
            text: `Remains unresolved: ${open.map((row) => row.label).join("; ")}.`,
            citationIds: open.map((row) => row.citationId),
        });
    }
    if (items.length === 0) {
        items.push({
            text: clip(bundle.canonical.body.text || ident.label, 280),
            citationIds: [
                bundle.canonical.body.text ? bundle.canonical.body.citationId : ident.citationId,
            ],
        });
    }
    return items.slice(0, CURRENT_POSITION_TARGET_SENTENCES);
}
export function keepGroundedCurrentPositionItems(items, allowedCitationIds) {
    return items.filter((item) => {
        if (!item.text.trim() || item.citationIds.length === 0)
            return false;
        return item.citationIds.every((id) => allowedCitationIds.has(id));
    });
}
export function parseCurrentPositionModelOutput(raw) {
    const trimmed = raw.trim();
    const fenced = trimmed.match(/\{[\s\S]*\}/);
    const jsonText = fenced?.[0] ?? trimmed;
    try {
        return CurrentPositionModelOutputSchema.parse(JSON.parse(jsonText));
    }
    catch {
        return null;
    }
}
export function projectSubjectContextForCurrentPosition(bundle) {
    const projected = projectSubjectContextForAsk(bundle, SUBJECT_ASK_PROMPT_CHAR_BUDGET, {
        includeConnected: true,
    });
    const current = selectCurrentFacts(bundle.canonical.facts);
    const currentBlock = current.length === 0
        ? "No structured facts are current."
        : current
            .map((group) => group.competing
            ? `CURRENT FACT ${group.key} COMPETING (do not resolve): ${group.facts
                .map((fact) => `[${fact.citationId}] ${fact.value}`)
                .join(" | ")}`
            : `CURRENT FACT ${group.key}: [${group.facts[0]?.citationId}] ${group.facts[0]?.value} asOf=${group.facts[0]?.asOf}`)
            .join("\n");
    const connectedLines = [];
    for (const origin of bundle.connected.origins) {
        for (const fact of origin.facts) {
            const citationId = connectedFactCitationId(origin.originSubjectId, fact.citationId);
            connectedLines.push(`ORIGIN ${origin.label} (${origin.kind}): CURRENT FACT ${fact.key}: [${citationId}] ${fact.value}${fact.unit ? ` ${fact.unit}` : ""} asOf=${fact.asOf}`);
        }
        for (const decision of origin.decisions) {
            const citationId = connectedDecisionCitationId(origin.originSubjectId, decision.citationId);
            connectedLines.push(`ORIGIN ${origin.label} (${origin.kind}): DECISION: [${citationId}] ${decision.text}`);
        }
    }
    const connectedBlock = connectedLines.length === 0
        ? null
        : ["## CURRENT CONNECTED STATE FOR PRESENTATION", ...connectedLines].join("\n");
    return [
        CURRENT_POSITION_DISCIPLINE,
        projected.prompt,
        "## CURRENT STRUCTURED STATE FOR PRESENTATION",
        currentBlock,
        ...(connectedBlock ? [connectedBlock] : []),
    ].join("\n\n");
}
export function composeCurrentPositionResult(input) {
    const sufficiency = assessCurrentPositionSufficiency(input.bundle);
    return CurrentPositionResultSchema.parse({
        status: sufficiency.status,
        origin: input.origin,
        generatedAt: input.generatedAt ?? new Date().toISOString(),
        subjectId: input.bundle.subjectId,
        bundleVersion: 1,
        bundleIdentity: subjectBundleContentIdentity(input.bundle),
        truncated: input.bundle.truncated,
        items: input.items,
        insufficientReason: sufficiency.reason,
    });
}
//# sourceMappingURL=subject-current-position.js.map